import type { EntitySaveCoordinator } from "./EntitySaveCoordinator";

class CoordinatorRegistry {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private coordinators = new Map<string, EntitySaveCoordinator<any, any>>();
  private flushers = new Map<string, Set<() => Promise<void>>>();
  private listeners = new Set<() => void>();

  register(
    entityId: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    coordinator: EntitySaveCoordinator<any, any>,
  ): void {
    this.coordinators.set(entityId, coordinator);
    this.notifyListeners();
  }

  unregister(entityId: string): void {
    this.coordinators.delete(entityId);
    this.flushers.delete(entityId);
    this.notifyListeners();
  }

  get(
    entityId: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): EntitySaveCoordinator<any, any> | undefined {
    return this.coordinators.get(entityId);
  }

  clear(): void {
    this.coordinators.clear();
    this.flushers.clear();
    this.notifyListeners();
  }

  registerFlusher(entityId: string, flusher: () => Promise<void>): () => void {
    const current = this.flushers.get(entityId) ?? new Set<() => Promise<void>>();
    current.add(flusher);
    this.flushers.set(entityId, current);
    return () => {
      current.delete(flusher);
      if (current.size === 0) {
        this.flushers.delete(entityId);
      }
    };
  }

  async flush(entityId: string): Promise<void> {
    const flushers = Array.from(this.flushers.get(entityId) ?? []);
    await Promise.all(flushers.map((flusher) => flusher()));
    await this.coordinators.get(entityId)?.flushPendingChanges();
  }

  /** Subscribe to registry changes (coordinator added/removed). */
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  private notifyListeners(): void {
    for (const listener of this.listeners) {
      listener();
    }
  }
}

export const coordinatorRegistry = new CoordinatorRegistry();
